import {useContext} from 'react';

import {useTranslation} from 'react-i18next';

import {TargetProfileDialogSectionCard} from './TargetProfileDialogSectionCard';
import {TargetProfileSkillSection} from './TargetProfileSkillSection';
import {LanguageContext} from '../../../../../contexts/LanguageContext/LanguageContext';
import type {StandardSkill} from '../../../profile/types';
import type {
  SkillCategory,
  TargetProfileDetail,
  TargetProfileSkill,
} from '../../types/DepartmentTargetProfileTypes';

export interface Props {
  profile: TargetProfileDetail;
  skillCurationDisabled?: boolean;
  onRemoveSkill?: (standardSkillId: number) => void;
  onToggleRequirementLevel?: (skill: TargetProfileSkill) => void;
  onAddSkill?: (
    skillCategory: SkillCategory,
    skill: StandardSkill,
    requirementLevel: 'must_have' | 'nice_to_have'
  ) => void;
  onAddCustomSkill?: (
    skillCategory: SkillCategory,
    name: string,
    requirementLevel: 'must_have' | 'nice_to_have'
  ) => void;
}

export const TargetProfileDialogSkillsSection = ({
  profile,
  skillCurationDisabled,
  onRemoveSkill,
  onToggleRequirementLevel,
  onAddSkill,
  onAddCustomSkill,
}: Props) => {
  const currentLanguage = useContext(LanguageContext);
  const {t} = useTranslation('translation', {keyPrefix: 'companySettings.departments.modal.targetProfiles'});
  const {t: tDetail} = useTranslation('translation', {
    keyPrefix: 'companySettings.departments.modal.targetProfiles.detail',
  });

  const isCurationEditable = Boolean(onRemoveSkill && onAddSkill && onAddCustomSkill);
  const removeSkillLabel = String(tDetail('removeSkill'));
  const toggleRequirementLabel = String(tDetail('toggleRequirement'));
  const addSkillLabel = String(tDetail('addSkill'));
  const createSkillLabel = (name: string) => String(tDetail('createSkill', {name}));
  const cancelAddSkillLabel = String(t('viewDialog.cancelAddSkill'));

  const sections: Array<{
    label: string;
    skillCategory: SkillCategory;
    skills: TargetProfileSkill[];
  }> = [
    {
      label: String(tDetail('coreCompetencies')),
      skillCategory: 'core_competency',
      skills: profile.coreCompetencies ?? [],
    },
    {
      label: String(tDetail('hardSkills')),
      skillCategory: 'hard_skill',
      skills: profile.hardSkills ?? [],
    },
    {
      label: String(tDetail('tools')),
      skillCategory: 'tool',
      skills: profile.tools ?? [],
    },
    {
      label: String(tDetail('domainKnowledge')),
      skillCategory: 'domain_knowledge',
      skills: profile.domainKnowledge ?? [],
    },
    {
      label: String(tDetail('softSkills')),
      skillCategory: 'soft_skill',
      skills: profile.softSkills ?? [],
    },
    {
      label: String(tDetail('responsibilities')),
      skillCategory: 'responsibility',
      skills: profile.responsibilities ?? [],
    },
  ];

  return (
    <>
      {sections.map((section) => (
        <TargetProfileDialogSectionCard key={section.skillCategory} title={section.label}>
          <TargetProfileSkillSection
            label={section.label}
            hideLabel
            skills={section.skills}
            language={currentLanguage}
            mustHaveLabel={tDetail('mustHave')}
            niceToHaveLabel={tDetail('niceToHave')}
            disabled={skillCurationDisabled}
            {...(isCurationEditable && {
              onRemoveSkill,
              onToggleRequirementLevel,
              onAddSkill: (skill: StandardSkill, requirementLevel: 'must_have' | 'nice_to_have') =>
                onAddSkill?.(section.skillCategory, skill, requirementLevel),
              onAddCustomSkill: (name: string, requirementLevel: 'must_have' | 'nice_to_have') =>
                onAddCustomSkill?.(section.skillCategory, name, requirementLevel),
              skillCategory: section.skillCategory,
              occupationResourceId: profile.occupation.resourceId,
              removeSkillLabel,
              toggleRequirementLabel,
              addSkillLabel,
              cancelAddSkillLabel,
              createSkillLabel,
              collapseAddControlsByDefault: true,
            })}
          />
        </TargetProfileDialogSectionCard>
      ))}
    </>
  );
};
